import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { SmilePair } from './SmileGallery';
import VideoReveal from './VideoReveal';

interface HeroSectionProps {
    onLaunchApp: () => void;
    onImageClick: (pair: SmilePair) => void;
    smilePairsCol1: SmilePair[];
    smilePairsCol2: SmilePair[];
    smilePairsCol3: SmilePair[];
}

const HeroSection: React.FC<HeroSectionProps> = memo(({ onLaunchApp, onImageClick, smilePairsCol1, smilePairsCol2, smilePairsCol3 }) => {

    // Combine all pairs to distribute them across the 5 columns
    const allPairs = [...smilePairsCol1, ...smilePairsCol2, ...smilePairsCol3];

    const [isVideoOpen, setIsVideoOpen] = React.useState(false);

    // Helper to get image and pair for a column (safely)
    const getImage = (index: number) => allPairs[index % allPairs.length]?.after || '';
    const getPair = (index: number) => allPairs[index % allPairs.length];

    // Card configurations for "Hero Focus" Layout (Mountain/Pyramid)
    // Refactored to use Tailwind responsive prefixes for better performance and resize support
    const cardConfigs = [
        {
            // Outer Left (Smallest)
            zIndex: 10,
            delay: 0.3,
            className: 'w-40 md:w-56 aspect-square opacity-80 hover:opacity-100 hover:z-40 transition-all duration-500 md:-mr-12 md:scale-[0.85] md:translate-y-8',
            mobileClassName: 'min-w-[200px] snap-center',
        },
        {
            // Inner Left (Medium)
            zIndex: 20,
            delay: 0.2,
            className: 'w-56 md:w-72 aspect-[4/5] opacity-90 hover:opacity-100 hover:z-40 transition-all duration-500 md:-mr-12 md:scale-[0.92] md:translate-y-4',
            mobileClassName: 'min-w-[240px] snap-center',
        },
        {
            // Center (Hero - Largest)
            zIndex: 30,
            delay: 0.1,
            className: 'w-72 md:w-96 aspect-[3/4] shadow-[0_30px_90px_rgba(20,184,166,0.4)] z-30 md:scale-100',
            mobileClassName: 'min-w-[280px] snap-center',
            isHero: true,
        },
        {
            // Inner Right (Medium)
            zIndex: 20,
            delay: 0.2,
            className: 'w-56 md:w-72 aspect-[4/5] opacity-90 hover:opacity-100 hover:z-40 transition-all duration-500 md:-ml-12 md:scale-[0.92] md:translate-y-4',
            mobileClassName: 'min-w-[240px] snap-center',
        },
        {
            // Outer Right (Smallest)
            zIndex: 10,
            delay: 0.3,
            className: 'w-40 md:w-56 aspect-square opacity-80 hover:opacity-100 hover:z-40 transition-all duration-500 md:-ml-12 md:scale-[0.85] md:translate-y-8',
            mobileClassName: 'min-w-[200px] snap-center',
        },
    ];

    return (
        <section className="relative z-10 pt-32 pb-20 flex flex-col items-center justify-center min-h-screen overflow-hidden" aria-label="Hero Section">

            {/* Main Content */}
            <div className="relative z-10 text-center px-4 max-w-5xl mx-auto mb-16 md:mb-20">

                {/* Enhanced Badge */}
                <motion.div
                    initial={{ opacity: 0, y: 20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.7, ease: [0.23, 1, 0.32, 1] }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-900/30 backdrop-blur-md border border-teal-500/30 text-teal-300 text-xs font-semibold mb-8 tracking-wide uppercase shadow-[0_0_20px_rgba(20,184,166,0.15)] hover:bg-teal-900/40 transition-colors cursor-default"
                >
                    <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse shadow-[0_0_8px_rgba(45,212,191,0.8)]"></span>
                    AI-Powered Dental Simulation
                </motion.div>

                {/* New Headline */}
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1, ease: [0.23, 1, 0.32, 1] }}
                    className="font-display text-5xl md:text-7xl lg:text-[5.5rem] font-extrabold leading-[1.05] tracking-tight mb-8 text-white"
                >
                    Turn Consultations Into{' '}
                    <span className="relative inline-block">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 via-teal-400 to-emerald-400 filter drop-shadow-[0_0_15px_rgba(45,212,191,0.4)]">
                            Commitments.
                        </span>
                        <motion.div
                            className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-teal-400/0 via-teal-400/50 to-teal-400/0 rounded-full"
                            initial={{ scaleX: 0 }}
                            animate={{ scaleX: 1 }}
                            transition={{ duration: 0.8, delay: 0.5 }}
                        ></motion.div>
                    </span>
                </motion.h1>

                {/* Subheadline */}
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.25, ease: [0.23, 1, 0.32, 1] }}
                    className="text-gray-300 text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto mb-8 font-light leading-relaxed"
                >
                    The world's most advanced AI smile simulation platform. Generate{' '}
                    <span className="text-white font-semibold">photorealistic, clinically accurate</span>{' '}
                    treatment previews in seconds—and show your patients exactly why they should say{' '}
                    <span className="text-teal-300 font-semibold">"Yes."</span>
                </motion.p>

                {/* CTA Buttons */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.35, ease: [0.23, 1, 0.32, 1] }}
                    className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-5 mb-6 flex-wrap"
                >
                    {/* Primary CTA */}
                    <button
                        onClick={onLaunchApp}
                        className="group relative px-10 py-5 bg-gradient-to-r from-teal-400 to-teal-500 text-[#050505] rounded-full font-bold text-lg hover:from-teal-300 hover:to-teal-400 transition-all duration-300 shadow-[0_0_25px_rgba(45,212,191,0.5)] hover:shadow-[0_0_40px_rgba(45,212,191,0.7)] hover:-translate-y-1 hover:scale-105 active:scale-[0.98] will-change-transform"
                    >
                        <span className="relative z-10 flex items-center gap-2">
                            Get Started
                            <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                        </span>
                        <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 rounded-full transition-opacity"></div>
                    </button>

                    {/* Secondary CTA */}
                    <button
                        onClick={() => setIsVideoOpen(true)}
                        className="group px-8 py-5 rounded-full text-white font-semibold hover:text-teal-300 flex items-center gap-3 transition-all duration-300 hover:gap-4 border border-white/10 hover:border-teal-500/30 backdrop-blur-sm bg-white/5 hover:bg-white/10"
                    >
                        <div className="relative">
                            <div className="absolute inset-0 bg-teal-400 blur-lg opacity-0 group-hover:opacity-30 transition-opacity"></div>
                            <svg className="w-6 h-6 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                        See How It Works
                    </button>

                    {/* Brochure CTA */}
                    <button
                        onClick={() => window.open('/brochure.html', '_blank')}
                        className="group px-8 py-5 rounded-full text-zinc-400 font-semibold hover:text-teal-300 flex items-center gap-3 transition-all duration-300 hover:gap-4 border border-white/5 hover:border-teal-500/30 backdrop-blur-sm bg-black/20 hover:bg-black/40"
                    >
                        <div className="relative">
                            <div className="absolute inset-0 bg-teal-400 blur-lg opacity-0 group-hover:opacity-30 transition-opacity"></div>
                            <svg className="w-6 h-6 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        Brochure
                    </button>
                </motion.div>

                {/* Micro-proof */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400 mb-2 md:mb-4"
                >
                    <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span className="font-medium text-gray-300">HIPAA Compliant</span>
                    </div>
                    <span className="text-gray-600">•</span>
                    <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span className="font-medium text-gray-300">30-Second Results</span>
                    </div>
                </motion.div>
            </div>

            {/* Video Reveal Section - Between Text and Gallery */}
            <div className="relative z-20 px-4 w-full">
                <VideoReveal
                    isOpen={isVideoOpen}
                    onOpen={() => setIsVideoOpen(true)}
                    onClose={() => setIsVideoOpen(false)}
                />
            </div>

            {/* "Hero Focus" Gallery Layout */}
            <div className="w-full relative mt-auto z-10 pb-12 md:pb-20 overflow-visible">

                {/* Spotlight Effect behind Center */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-500/10 blur-[120px] rounded-full pointer-events-none"></div>

                {/* Container - Flex for Desktop (Overlap), Scroll for Mobile */}
                <div className="flex md:justify-center items-center overflow-x-auto md:overflow-visible pb-8 md:pb-0 px-4 md:px-0 snap-x snap-mandatory scrollbar-hide gap-4 md:gap-0">

                    {cardConfigs.map((config, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 100, scale: 0.8 }}
                            animate={{
                                opacity: 1,
                                y: 0,
                                scale: 1,
                            }}
                            transition={{
                                duration: 1,
                                delay: config.delay,
                                ease: [0.22, 1, 0.36, 1]
                            }}
                            onClick={() => onImageClick(getPair(index))}
                            className={`relative group shrink-0 rounded-[1.5rem] overflow-hidden cursor-pointer flex flex-col items-center justify-start pt-8 md:pt-10
                                       shadow-[0_20px_60px_rgba(0,0,0,0.5)] hover:shadow-[0_40px_100px_rgba(20,184,166,0.3)]
                                       border-2 border-white/5 hover:border-teal-500/40 backdrop-blur-xl
                                       ${config.className} ${config.mobileClassName}`}
                            style={{
                                zIndex: config.zIndex,
                                willChange: 'transform, opacity', // GPU acceleration hint
                            }}
                        >
                            {/* Inner Border Ring for "Framed Art" look */}
                            <div className="absolute inset-2 rounded-[1.2rem] border border-white/10 pointer-events-none z-20"></div>

                            {/* Image - GPU-accelerated for smooth hover */}
                            <div className="absolute inset-0 z-10 transition-transform duration-700 will-change-transform group-hover:scale-110">
                                <img
                                    src={getImage(index)}
                                    alt={`AI Smile Simulation - Case ${index + 1} After Result`}
                                    className="w-full h-full object-cover"
                                    loading={config.isHero ? "eager" : "lazy"}
                                />
                            </div>

                            {/* Done Status Badge - Floating */}
                            <div className="relative z-30 flex items-center gap-2.5 bg-black/60 backdrop-blur-xl border border-white/10 rounded-full px-4 py-2 shadow-2xl overflow-hidden group-hover:bg-black/70 transition-colors">
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] animate-[shimmer_3s_infinite]"></div>
                                <svg className="w-5 h-5 text-[#00ff88] drop-shadow-[0_0_8px_rgba(0,255,136,0.5)]" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1.177-7.86l-2.765-2.767L7 12.431l3.119 3.121a1 1 0 001.414 0l5.952-5.95-1.062-1.062-5.6 5.6z"></path>
                                </svg>
                                <span className="text-white font-semibold tracking-wide text-xs md:text-sm uppercase">✓ Complete</span>
                            </div>

                            {/* Shine Effect */}
                            <div className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-30"
                                style={{
                                    background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 60%)'
                                }}></div>
                        </motion.div>
                    ))}

                </div>
            </div>
        </section >
    );
});

export default HeroSection;
